import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const StatCard = ({ title, value, sub, iconPath, color }) => (
  <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5 flex items-center gap-4">
    <div className={`w-12 h-12 rounded-2xl flex items-center justify-center flex-shrink-0 ${color}`}>
      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={iconPath} />
      </svg>
    </div>
    <div className="min-w-0">
      <p className="text-2xl font-bold text-slate-900 leading-none">{value}</p>
      <p className="text-sm font-medium text-slate-600 mt-1">{title}</p>
      {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
    </div>
  </div>
);

const ProgressRow = ({ label, value, max, colorClass }) => {
  const pct = max > 0 ? Math.round((value / max) * 100) : 0;
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-sm text-slate-600">{label}</span>
        <span className="text-sm font-semibold text-slate-800">{value} <span className="text-slate-400 font-normal">/ {max}</span></span>
      </div>
      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
        <div className={`h-2 rounded-full transition-all duration-500 ${colorClass}`} style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

const Dashboard = () => {
  const [pendingSubscriptionsCount, setPendingSubscriptionsCount] = useState(0);
  const [stats, setStats] = useState({
    totalUsers: 0, totalBatches: 0, totalPhotos: 0, pendingApprovals: 0,
    approvedPhotos: 0, rejectedPhotos: 0, totalVerifications: 0,
    verificationsWithMatches: 0, totalMatches: 0, encodedPhotos: 0,
    unencodedPhotos: 0, activeUploadLinks: 0, activeVerifyLinks: 0,
    approvedBatches: 0, pendingBatches: 0, rejectedBatches: 0,
    deletedPhotos: 0, deletedBatches: 0, deletedUsers: 0,
    totalDeletionEvents: 0, lastDeletionAt: null,
  });
  const [loading, setLoading] = useState(true);
  const [recentVerifications, setRecentVerifications] = useState([]);
  const [recentDeletions, setRecentDeletions] = useState([]);

  useEffect(() => { fetchStats(); }, []);

  const fetchStats = async () => {
    try {
      const [usersRes, batchesRes, photosRes, verificationsRes, deletionsRes, pendingCountRes] = await Promise.all([
        axios.get(`${API_URL}/users`),
        axios.get(`${API_URL}/batches`),
        axios.get(`${API_URL}/photos`),
        axios.get(`${API_URL}/verifications`),
        axios.get(`${API_URL}/logs/deletions/summary`),
        axios.get(`${API_URL}/subscriptions/admin/pending-count`).catch(() => ({ data: { count: 0 } })),
      ]);
      setPendingSubscriptionsCount(pendingCountRes.data?.count ?? 0);
      const users = usersRes.data;
      const batches = batchesRes.data;
      const photos = photosRes.data;
      const verifications = verificationsRes.data;
      const approvedPhotos = photos.filter(p => p.status === 'approved');
      const encodedPhotos = approvedPhotos.filter(p => p.is_encoded);
      const unencodedPhotos = approvedPhotos.filter(p => !p.is_encoded);
      const verificationsWithMatches = verifications.filter(v => v.matches_found > 0);
      const totalMatches = verifications.reduce((sum, v) => sum + (v.matches_found || 0), 0);
      const activeUploadLinks = users.filter(u => u.upload_link_active === true || u.upload_link_active === 1).length;
      const activeVerifyLinks = users.filter(u => u.verify_link_active === true || u.verify_link_active === 1).length;
      const recent = [...verifications].sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).slice(0, 5);
      const deletionSummary = deletionsRes.data?.summary || {};
      const deletionRecent = deletionsRes.data?.recent || [];
      setStats({
        totalUsers: users.length, totalBatches: batches.length, totalPhotos: photos.length,
        pendingApprovals: photos.filter(p => p.status === 'pending').length,
        approvedPhotos: approvedPhotos.length, rejectedPhotos: photos.filter(p => p.status === 'rejected').length,
        totalVerifications: verifications.length, verificationsWithMatches: verificationsWithMatches.length,
        totalMatches, encodedPhotos: encodedPhotos.length, unencodedPhotos: unencodedPhotos.length,
        activeUploadLinks, activeVerifyLinks,
        approvedBatches: batches.filter(b => b.status === 'approved').length,
        pendingBatches: batches.filter(b => b.status === 'pending').length,
        rejectedBatches: batches.filter(b => b.status === 'rejected').length,
        deletedPhotos: deletionSummary.total_photos_deleted || 0,
        deletedBatches: deletionSummary.total_batches_deleted || 0,
        deletedUsers: deletionSummary.total_users_affected || 0,
        totalDeletionEvents: deletionSummary.total_deletions || 0,
        lastDeletionAt: deletionSummary.last_deletion_at || null,
      });
      setRecentVerifications(recent);
      setRecentDeletions(deletionRecent);
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[50vh]">
        <div className="text-center">
          <div className="w-12 h-12 rounded-2xl bg-violet-50 flex items-center justify-center mx-auto mb-3">
            <div className="w-6 h-6 border-2 border-violet-600 border-t-transparent rounded-full animate-spin" />
          </div>
          <p className="text-slate-500 text-sm">Loading dashboard…</p>
        </div>
      </div>
    );
  }

  const photoApprovalRate = stats.totalPhotos > 0 ? ((stats.approvedPhotos / stats.totalPhotos) * 100).toFixed(1) : 0;
  const encodingRate = stats.approvedPhotos > 0 ? ((stats.encodedPhotos / stats.approvedPhotos) * 100).toFixed(1) : 0;
  const matchRate = stats.totalVerifications > 0 ? ((stats.verificationsWithMatches / stats.totalVerifications) * 100).toFixed(1) : 0;

  return (
    <div className="space-y-6">
      {/* Page header */}
      <div className="bg-white rounded-2xl overflow-hidden border border-slate-200 shadow-sm">
        <div className="px-6 py-5" style={{ background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 100%)' }}>
          <h1 className="text-xl font-bold text-white">Dashboard</h1>
          <p className="text-violet-300 text-sm mt-0.5">System overview & analytics</p>
        </div>
        {pendingSubscriptionsCount > 0 && (
          <div className="px-6 py-3 bg-amber-50 border-t border-amber-200 flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="w-8 h-8 rounded-xl bg-amber-400 flex items-center justify-center text-amber-900 font-bold text-sm flex-shrink-0">
                {pendingSubscriptionsCount}
              </span>
              <p className="text-sm font-semibold text-amber-900">
                {pendingSubscriptionsCount} subscription{pendingSubscriptionsCount !== 1 ? 's' : ''} pending approval
              </p>
            </div>
            <Link to="/subscriptions" className="text-xs font-semibold text-amber-900 bg-amber-200 hover:bg-amber-300 px-3 py-1.5 rounded-xl transition-colors">
              Review →
            </Link>
          </div>
        )}
      </div>

      {/* Primary stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <StatCard title="Total Users" value={stats.totalUsers} color="bg-violet-600"
          iconPath="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
        <StatCard title="Total Batches" value={stats.totalBatches} color="bg-blue-600"
          iconPath="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
        <StatCard title="Total Photos" value={stats.totalPhotos} color="bg-emerald-600"
          iconPath="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
        <StatCard title="Verifications" value={stats.totalVerifications} color="bg-amber-600"
          iconPath="M15 12a3 3 0 11-6 0 3 3 0 016 0zM2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
      </div>

      {/* Secondary stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <StatCard title="Pending Approvals" value={stats.pendingApprovals}
          sub={`${((stats.pendingApprovals / stats.totalPhotos) * 100 || 0).toFixed(1)}% of photos`}
          color="bg-amber-500"
          iconPath="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        <StatCard title="Approved Photos" value={stats.approvedPhotos}
          sub={`${photoApprovalRate}% approval rate`}
          color="bg-emerald-500"
          iconPath="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
        <StatCard title="Total Matches" value={stats.totalMatches}
          sub={`${stats.verificationsWithMatches} verified with matches`}
          color="bg-violet-500"
          iconPath="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
        <StatCard title="Encoded Photos" value={stats.encodedPhotos}
          sub={`${encodingRate}% of approved`}
          color="bg-blue-500"
          iconPath="M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4" />
      </div>

      {/* Progress cards */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
          <h2 className="text-sm font-bold text-slate-800 mb-4">Photo Status</h2>
          <div className="space-y-3">
            <ProgressRow label="Approved" value={stats.approvedPhotos} max={stats.totalPhotos} colorClass="bg-emerald-500" />
            <ProgressRow label="Pending" value={stats.pendingApprovals} max={stats.totalPhotos} colorClass="bg-amber-400" />
            <ProgressRow label="Rejected" value={stats.rejectedPhotos} max={stats.totalPhotos} colorClass="bg-rose-500" />
          </div>
          <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-3 gap-2 text-center">
            {[
              { label: 'Approved', val: stats.approvedPhotos, cls: 'text-emerald-600' },
              { label: 'Pending', val: stats.pendingApprovals, cls: 'text-amber-600' },
              { label: 'Rejected', val: stats.rejectedPhotos, cls: 'text-rose-600' },
            ].map(({ label, val, cls }) => (
              <div key={label}>
                <p className={`text-xl font-bold ${cls}`}>{val}</p>
                <p className="text-xs text-slate-400 mt-0.5">{label}</p>
              </div>
            ))}
          </div>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
          <h2 className="text-sm font-bold text-slate-800 mb-4">Encoding Status</h2>
          <div className="space-y-3">
            <ProgressRow label="Encoded" value={stats.encodedPhotos} max={stats.approvedPhotos} colorClass="bg-blue-500" />
            <ProgressRow label="Pending encoding" value={stats.unencodedPhotos} max={stats.approvedPhotos} colorClass="bg-orange-400" />
          </div>
          <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-2 gap-2 text-center">
            {[
              { label: 'Encoded', val: stats.encodedPhotos, cls: 'text-blue-600' },
              { label: 'Pending', val: stats.unencodedPhotos, cls: 'text-orange-600' },
            ].map(({ label, val, cls }) => (
              <div key={label}>
                <p className={`text-xl font-bold ${cls}`}>{val}</p>
                <p className="text-xs text-slate-400 mt-0.5">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Batch status + recent verifications + recent deletions */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Batch status */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
          <h2 className="text-sm font-bold text-slate-800 mb-4">Batch Status</h2>
          <div className="space-y-2">
            {[
              { label: 'Approved', val: stats.approvedBatches, bg: 'bg-emerald-50', text: 'text-emerald-700', dot: 'bg-emerald-500' },
              { label: 'Pending', val: stats.pendingBatches, bg: 'bg-amber-50', text: 'text-amber-700', dot: 'bg-amber-500' },
              { label: 'Rejected', val: stats.rejectedBatches, bg: 'bg-rose-50', text: 'text-rose-700', dot: 'bg-rose-500' },
            ].map(({ label, val, bg, text, dot }) => (
              <div key={label} className={`flex items-center justify-between px-4 py-3 rounded-xl ${bg}`}>
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${dot}`} />
                  <span className={`text-sm font-medium ${text}`}>{label}</span>
                </div>
                <span className={`text-xl font-bold ${text}`}>{val}</span>
              </div>
            ))}
          </div>
          <div className="mt-4 pt-3 border-t border-slate-100 grid grid-cols-2 gap-2 text-center text-xs text-slate-500">
            <div>
              <p className="font-semibold text-slate-700">{matchRate}%</p>
              <p>match rate</p>
            </div>
            <div>
              <p className="font-semibold text-slate-700">{stats.verificationsWithMatches}</p>
              <p>with matches</p>
            </div>
          </div>
        </div>

        {/* Recent verifications */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
          <h2 className="text-sm font-bold text-slate-800 mb-4">Recent Verifications</h2>
          {recentVerifications.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-6">No verifications yet</p>
          ) : (
            <div className="space-y-2">
              {recentVerifications.map((v) => (
                <div key={v.id} className="flex items-center justify-between gap-2 py-2 border-b border-slate-50 last:border-0">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{v.user_name}</p>
                    <p className="text-xs text-slate-400">{new Date(v.created_at).toLocaleString()}</p>
                  </div>
                  {v.matches_found > 0 ? (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-700 flex-shrink-0">
                      {v.matches_found} match{v.matches_found !== 1 ? 'es' : ''}
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold bg-slate-100 text-slate-500 flex-shrink-0">
                      No match
                    </span>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent deletions */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-5">
          <h2 className="text-sm font-bold text-slate-800 mb-4">Recent Deletions</h2>
          {recentDeletions.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-6">No deletions recorded</p>
          ) : (
            <div className="space-y-2">
              {recentDeletions.map((d) => (
                <div key={d.id} className="py-2 border-b border-slate-50 last:border-0">
                  <div className="flex justify-between items-start mb-1.5">
                    <div>
                      <p className="text-sm font-medium text-slate-800">Admin: {d.admin_username || 'Unknown'}</p>
                      <p className="text-xs text-slate-400">{new Date(d.created_at).toLocaleString()}</p>
                    </div>
                    <span className="text-xs text-slate-400">Batch #{d.batch_id}</span>
                  </div>
                  <div className="grid grid-cols-3 gap-1.5">
                    {[
                      { label: 'Photos', val: d.photos_deleted },
                      { label: 'Batches', val: d.batches_deleted },
                      { label: 'Users', val: d.users_affected },
                    ].map(({ label, val }) => (
                      <div key={label} className="bg-slate-50 rounded-lg py-1.5 text-center">
                        <p className="text-sm font-bold text-slate-800">{val}</p>
                        <p className="text-[10px] text-slate-400 uppercase tracking-wide">{label}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Deletion impact */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        <StatCard title="Photos Deleted" value={stats.deletedPhotos}
          sub={stats.totalPhotos > 0 ? `${((stats.deletedPhotos / (stats.totalPhotos + stats.deletedPhotos)) * 100).toFixed(1)}% of lifetime` : undefined}
          color="bg-slate-600"
          iconPath="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        <StatCard title="Batches Deleted" value={stats.deletedBatches}
          color="bg-slate-700"
          iconPath="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        <StatCard title="Users Affected" value={stats.deletedUsers}
          sub="By deletions"
          color="bg-slate-500"
          iconPath="M13 7a4 4 0 11-8 0 4 4 0 018 0zM9 14a6 6 0 00-6 6v1h12v-1a6 6 0 00-6-6zM21 12h-6" />
        <StatCard title="Deletion Events" value={stats.totalDeletionEvents}
          sub={stats.lastDeletionAt ? `Last: ${new Date(stats.lastDeletionAt).toLocaleDateString()}` : 'No deletions yet'}
          color="bg-rose-700"
          iconPath="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </div>
    </div>
  );
};

export default Dashboard;
